import { WidgetType } from "./types";

/**
 * Layout preset library for the Template Designer.
 *
 * Every zone is expressed in PERCENTAGES of the canvas (0..100) using the
 * same x/y/width/height model the rest of the designer stores, so a preset
 * applies identically in landscape or portrait and can never leave the
 * canvas. Presets tile the canvas edge-to-edge, so zones within a preset
 * never overlap.
 */

export type PresetCategory = "basic" | "media" | "restaurant" | "information" | "vmpos";

export interface PresetZone {
  name: string;
  widgetType: WidgetType;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
  /** Optional overrides merged over the widget's default config on apply. */
  config?: Record<string, unknown>;
}

export interface LayoutPreset {
  id: string;
  name: string;
  category: PresetCategory;
  zones: PresetZone[];
}

const z = (
  name: string,
  widgetType: WidgetType,
  x: number,
  y: number,
  width: number,
  height: number,
  zIndex = 0,
  config?: Record<string, unknown>,
): PresetZone => ({ name, widgetType, x, y, width, height, zIndex, config });

/** Menu board zone backed by the VM POS product feed. */
const posMenu = (
  name: string, x: number, y: number, width: number, height: number, columns = 1,
): PresetZone => z(name, "menu_board", x, y, width, height, 0, { source: "vmpos", columns });

export const CATEGORY_LABELS: Record<PresetCategory, string> = {
  basic: "Basic",
  media: "Media",
  restaurant: "Restaurant",
  information: "Information",
  vmpos: "VM POS",
};

export const LAYOUT_PRESETS: LayoutPreset[] = [
  /* ── Basic ──────────────────────────────────────────────────────────── */
  { id: "full-screen", name: "Full Screen", category: "basic", zones: [
    z("Content", "image", 0, 0, 100, 100),
  ]},
  { id: "two-cols", name: "Two Equal Columns", category: "basic", zones: [
    z("Left", "image", 0, 0, 50, 100),
    z("Right", "image", 50, 0, 50, 100),
  ]},
  { id: "two-rows", name: "Two Equal Rows", category: "basic", zones: [
    z("Top", "image", 0, 0, 100, 50),
    z("Bottom", "image", 0, 50, 100, 50),
  ]},
  { id: "main-sidebar", name: "Main Content + Sidebar", category: "basic", zones: [
    z("Main", "image", 0, 0, 70, 100),
    z("Sidebar", "image", 70, 0, 30, 100),
  ]},
  { id: "sidebar-main", name: "Sidebar + Main Content", category: "basic", zones: [
    z("Sidebar", "image", 0, 0, 30, 100),
    z("Main", "image", 30, 0, 70, 100),
  ]},
  { id: "three-cols", name: "Three Equal Columns", category: "basic", zones: [
    z("Left", "image", 0, 0, 33.33, 100),
    z("Center", "image", 33.33, 0, 33.33, 100),
    z("Right", "image", 66.66, 0, 33.34, 100),
  ]},
  { id: "three-rows", name: "Three Equal Rows", category: "basic", zones: [
    z("Top", "image", 0, 0, 100, 33.33),
    z("Middle", "image", 0, 33.33, 100, 33.33),
    z("Bottom", "image", 0, 66.66, 100, 33.34),
  ]},
  { id: "four-grid", name: "Four Grid", category: "basic", zones: [
    z("Top left", "image", 0, 0, 50, 50),
    z("Top right", "image", 50, 0, 50, 50),
    z("Bottom left", "image", 0, 50, 50, 50),
    z("Bottom right", "image", 50, 50, 50, 50),
  ]},
  { id: "large-top-two-bottom", name: "Large Top + Two Bottom", category: "basic", zones: [
    z("Top", "image", 0, 0, 100, 60),
    z("Bottom left", "image", 0, 60, 50, 40),
    z("Bottom right", "image", 50, 60, 50, 40),
  ]},
  { id: "two-top-large-bottom", name: "Two Top + Large Bottom", category: "basic", zones: [
    z("Top left", "image", 0, 0, 50, 40),
    z("Top right", "image", 50, 0, 50, 40),
    z("Bottom", "image", 0, 40, 100, 60),
  ]},
  { id: "large-left-two-right", name: "Large Left + Two Right", category: "basic", zones: [
    z("Left", "image", 0, 0, 60, 100),
    z("Right top", "image", 60, 0, 40, 50),
    z("Right bottom", "image", 60, 50, 40, 50),
  ]},
  { id: "two-left-large-right", name: "Two Left + Large Right", category: "basic", zones: [
    z("Left top", "image", 0, 0, 40, 50),
    z("Left bottom", "image", 0, 50, 40, 50),
    z("Right", "image", 40, 0, 60, 100),
  ]},

  /* ── Media ──────────────────────────────────────────────────────────── */
  { id: "2-image", name: "2 Image Layout", category: "media", zones: [
    z("Image 1", "image", 0, 0, 50, 100),
    z("Image 2", "image", 50, 0, 50, 100),
  ]},
  { id: "3-image", name: "3 Image Layout", category: "media", zones: [
    z("Image 1", "image", 0, 0, 33.33, 100),
    z("Image 2", "image", 33.33, 0, 33.33, 100),
    z("Image 3", "image", 66.66, 0, 33.34, 100),
  ]},
  { id: "4-image-grid", name: "4 Image Grid", category: "media", zones: [
    z("Image 1", "image", 0, 0, 50, 50),
    z("Image 2", "image", 50, 0, 50, 50),
    z("Image 3", "image", 0, 50, 50, 50),
    z("Image 4", "image", 50, 50, 50, 50),
  ]},
  { id: "2-video", name: "2 Video Layout", category: "media", zones: [
    z("Video 1", "video", 0, 0, 50, 100),
    z("Video 2", "video", 50, 0, 50, 100),
  ]},
  { id: "3-video", name: "3 Video Layout", category: "media", zones: [
    z("Video 1", "video", 0, 0, 33.33, 100),
    z("Video 2", "video", 33.33, 0, 33.33, 100),
    z("Video 3", "video", 66.66, 0, 33.34, 100),
  ]},
  { id: "video-image", name: "Video + Image", category: "media", zones: [
    z("Video", "video", 0, 0, 50, 100),
    z("Image", "image", 50, 0, 50, 100),
  ]},
  { id: "main-video-two-images", name: "Main Video + Two Images", category: "media", zones: [
    z("Main video", "video", 0, 0, 70, 100),
    z("Image 1", "image", 70, 0, 30, 50),
    z("Image 2", "image", 70, 50, 30, 50),
  ]},
  { id: "main-image-two-videos", name: "Main Image + Two Videos", category: "media", zones: [
    z("Main image", "image", 0, 0, 70, 100),
    z("Video 1", "video", 70, 0, 30, 50),
    z("Video 2", "video", 70, 50, 30, 50),
  ]},
  { id: "promotion-qr", name: "Promotion + QR Code", category: "media", zones: [
    z("Promotion", "image", 0, 0, 72, 100),
    z("QR code", "qr", 72, 0, 28, 100),
  ]},
  { id: "news-side-promo", name: "News / Website + Side Promotion", category: "media", zones: [
    z("Website", "website", 0, 0, 70, 100),
    z("Promotion", "image", 70, 0, 30, 100),
  ]},
  { id: "full-video-ticker", name: "Full Video + Bottom Ticker", category: "media", zones: [
    z("Video", "video", 0, 0, 100, 88),
    z("Ticker", "text", 0, 88, 100, 12),
  ]},

  /* ── Restaurant ─────────────────────────────────────────────────────── */
  { id: "video-menu", name: "Video + Menu Board", category: "restaurant", zones: [
    z("Video", "video", 0, 0, 60, 100),
    z("Menu", "menu_board", 60, 0, 40, 100),
  ]},
  { id: "menu-offer", name: "Menu Board + Offer Banner", category: "restaurant", zones: [
    z("Menu", "menu_board", 0, 0, 100, 82),
    z("Offer banner", "text", 0, 82, 100, 18),
  ]},
  { id: "menu-2col", name: "Digital Menu Board – 2 Columns", category: "restaurant", zones: [
    z("Menu left", "menu_board", 0, 0, 50, 100),
    z("Menu right", "menu_board", 50, 0, 50, 100),
  ]},
  { id: "menu-3col", name: "Digital Menu Board – 3 Columns", category: "restaurant", zones: [
    z("Menu 1", "menu_board", 0, 0, 33.33, 100),
    z("Menu 2", "menu_board", 33.33, 0, 33.33, 100),
    z("Menu 3", "menu_board", 66.66, 0, 33.34, 100),
  ]},
  { id: "combo-board", name: "Restaurant Combo Board", category: "restaurant", zones: [
    z("Combo promo", "video", 0, 0, 100, 50),
    z("Menu left", "menu_board", 0, 50, 50, 50),
    z("Menu right", "menu_board", 50, 50, 50, 50),
  ]},

  /* ── Information ────────────────────────────────────────────────────── */
  { id: "waiting-room", name: "Waiting Room Information Layout", category: "information", zones: [
    z("Welcome", "text", 0, 0, 100, 15),
    z("Information", "website", 0, 15, 65, 85),
    z("Weather", "weather", 65, 15, 35, 42.5),
    z("Clock", "clock", 65, 57.5, 35, 42.5),
  ]},

  /* ── VM POS Smart Templates ─────────────────────────────────────────── */
  /* Menu zones read from the VM POS product feed (config.source = "vmpos"). */
  { id: "pos-menu-2col", name: "Two-Column Menu", category: "vmpos", zones: [
    posMenu("Menu left", 0, 0, 50, 100),
    posMenu("Menu right", 50, 0, 50, 100),
  ]},
  { id: "pos-menu-3col", name: "Three-Column Menu", category: "vmpos", zones: [
    posMenu("Menu 1", 0, 0, 33.33, 100),
    posMenu("Menu 2", 33.33, 0, 33.33, 100),
    posMenu("Menu 3", 66.66, 0, 33.34, 100),
  ]},
  { id: "pos-menu-video", name: "Menu + Promotion Video", category: "vmpos", zones: [
    posMenu("Menu", 0, 0, 60, 100, 2),
    z("Promotion", "video", 60, 0, 40, 100),
  ]},
  { id: "pos-menu-image", name: "Menu + Product Image", category: "vmpos", zones: [
    posMenu("Menu", 0, 0, 60, 100, 2),
    z("Product image", "image", 60, 0, 40, 100),
  ]},
  { id: "pos-menu-qr", name: "Menu + QR Ordering", category: "vmpos", zones: [
    posMenu("Menu", 0, 0, 75, 100, 2),
    z("Order QR", "qr", 75, 0, 25, 100),
  ]},
  { id: "pos-dayparts", name: "Breakfast / Lunch / Dinner Schedule", category: "vmpos", zones: [
    posMenu("Breakfast", 0, 0, 33.33, 100),
    posMenu("Lunch", 33.33, 0, 33.33, 100),
    posMenu("Dinner", 66.66, 0, 33.34, 100),
  ]},
  { id: "pos-soldout", name: "Sold-Out-Aware Menu Board", category: "vmpos", zones: [
    posMenu("Live menu", 0, 0, 100, 100, 3),
  ]},
  { id: "pos-featured", name: "Featured Item + Category List", category: "vmpos", zones: [
    z("Featured item", "image", 0, 0, 45, 100),
    posMenu("Categories", 45, 0, 55, 100, 2),
  ]},
  { id: "pos-combo", name: "Combo Promotion Board", category: "vmpos", zones: [
    z("Combo promo", "video", 0, 0, 100, 45),
    posMenu("Menu left", 0, 45, 50, 55),
    posMenu("Menu right", 50, 45, 50, 55),
  ]},
  { id: "pos-order-status", name: "Order Status + Promotional Sidebar", category: "vmpos", zones: [
    posMenu("Order status", 0, 0, 65, 100),
    z("Promotion", "image", 65, 0, 35, 100),
  ]},
];

/** Do two percentage rectangles share any area? */
export const rectsOverlap = (
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
): boolean =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

/**
 * Can a preset's zones be ADDED to the existing layout without overlapping
 * any current zone? (Section 7: "Add preset zones to current layout" is only
 * enabled when the new zones do not overlap existing zones.)
 */
export const presetCanBeAdded = (
  preset: LayoutPreset,
  existing: Array<{ x: number; y: number; w: number; h: number }>,
): boolean =>
  preset.zones.every((pz) => {
    const r = { x: pz.x, y: pz.y, w: pz.width, h: pz.height };
    return existing.every((ez) => !rectsOverlap(r, ez));
  });
